import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import CircularProgress from '@mui/material/CircularProgress';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { lazy, Suspense, useCallback, useId, useMemo, useState } from 'react';
import { helpFor } from './catalog';
import { useGuideCatalog } from './guide-context';
import { HELP_SCREENS, type HelpScreen } from './screens';

// Il pannello rende il Markdown: è nel pezzo di bundle della Guida, caricato al primo «?» (FED-002).
const HelpPanel = lazy(() => import('./HelpPanel').then((module) => ({ default: module.HelpPanel })));

/**
 * Il «?» di una schermata (PRD-006 US-1, PAGE-010): apre a lato la pagina della Guida associata
 * alla schermata (campo `schermate`) e, sotto, le sue voci FAQ, senza lasciare il lavoro. Se la
 * Guida non ha pagine per la schermata non compare (la build lo segnala come avviso).
 */
export function HelpButton({ screen }: { screen: HelpScreen }) {
  const catalog = useGuideCatalog();
  const help = useMemo(() => helpFor(screen, catalog.pages), [screen, catalog]);
  const [open, setOpen] = useState(false);
  const close = useCallback(() => setOpen(false), []);
  const titleId = useId();

  if (!help.main && help.faq.length === 0) return null;
  return (
    <>
      <Tooltip title="Aiuto">
        <IconButton
          aria-label={`Aiuto: ${HELP_SCREENS[screen]}`}
          aria-haspopup="dialog"
          onClick={() => setOpen(true)}
          size="small"
          color="primary"
        >
          <HelpOutlineIcon />
        </IconButton>
      </Tooltip>
      <Drawer
        anchor="right"
        open={open}
        onClose={close}
        slotProps={{
          paper: { sx: { width: { xs: '100%', sm: 520 } }, role: 'dialog', 'aria-labelledby': titleId },
        }}
      >
        <Suspense
          fallback={
            <Stack direction="row" spacing={2} alignItems="center" role="status" sx={{ p: 6 }}>
              <CircularProgress size={20} />
              <Typography id={titleId}>Caricamento dell’aiuto…</Typography>
            </Stack>
          }
        >
          <HelpPanel screen={screen} help={help} titleId={titleId} onClose={close} />
        </Suspense>
      </Drawer>
    </>
  );
}
